import { ChevronRight } from "lucide-react";

function initialsFromDisplayName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

/** Texto «Sistema Empresarial» junto al logo BNT (sin repetir las letras BNT). */
function ProductTagline({ sizeKey, subColor, subAccent, className = "" }) {
  const taglineSizes = {
    sm: "text-[11px]",
    md: "text-sm",
    lg: "text-base",
    xl: "text-lg",
  };
  const cls = taglineSizes[sizeKey] || taglineSizes.md;
  return (
    <div
      className={`${cls} leading-tight tracking-wide font-semibold ${className}`}
      style={{ fontFamily: "'Chivo'" }}
    >
      <span className={subColor}>Sistema </span>
      <span className={subAccent}>Empresarial</span>
    </div>
  );
}

/** Subtítulo «BNT Visión y Logística Empresarial» bajo el título de login. */
export function LoginVisionTagline({ className = "" }) {
  return (
    <span className={`text-[10px] uppercase tracking-wider leading-snug text-[#0F2D5C]/75 ${className}`}>
      <span className="font-bold">
        <span className="text-[#0F2D5C]">B</span>
        <span className="text-[#3DA873]">N</span>
        <span className="text-[#0F2D5C]">T</span>
      </span>
      {" "}
      Visión y <span className="text-[#3DA873] font-semibold">Logística</span> Empresarial
    </span>
  );
}

/** Logo BNT — branding por defecto (login) o imagen cargada en Configuración. */
export function BNTLogo({
  size = "md",
  inverted = false,
  customLogo,
  customName,
  customSubtitle,
  showDefault = false,
  /** En login: «Sistema Empresarial» junto al logo (ignora nombre comercial). */
  productTagline = false,
}) {
  const sizes = {
    sm: { wrap: "gap-2", letters: "text-2xl", sub: "text-[9px]", tagline: "text-[10px]", empty: "min-h-9" },
    md: { wrap: "gap-2.5", letters: "text-3xl", sub: "text-[10px]", tagline: "text-xs", empty: "min-h-11" },
    lg: { wrap: "gap-3", letters: "text-5xl", sub: "text-xs", tagline: "text-sm", empty: "min-h-14" },
    xl: { wrap: "gap-4", letters: "text-7xl", sub: "text-sm", tagline: "text-base", empty: "min-h-20" },
  };
  const s = sizes[size];
  const blue = inverted ? "text-white" : "text-[#0F2D5C]";
  const green = "text-[#3DA873]";
  const sep = inverted ? "bg-white/40" : "bg-[#0F2D5C]/30";
  const subColor = inverted ? "text-white/80" : "text-[#0F2D5C]/80";
  const subAccent = "text-[#3DA873]";

  const logoSrc = (customLogo || "").trim();
  const nameText = (customName || "").trim();

  if (logoSrc) {
    const customImgH =
      size === "xl" ? "max-h-24" : size === "lg" ? "max-h-20" : size === "md" ? "max-h-14" : "max-h-10";
    const customNameCls =
      size === "xl"
        ? "text-sm"
        : size === "lg"
          ? "text-[13px]"
          : size === "md"
            ? "text-[11px]"
            : s.sub;
    const sideText = productTagline ? null : nameText;
    return (
      <div className={`flex items-center ${s.wrap}`} data-testid="custom-logo">
        <img src={logoSrc} alt={sideText || "Logo BNT"} className={`${customImgH} w-auto object-contain`} />
        {productTagline ? (
          <>
            <div className={`w-px self-stretch ${sep}`} />
            <ProductTagline sizeKey={size} subColor={subColor} subAccent={subAccent} />
          </>
        ) : sideText ? (
          <>
            <div className={`w-px self-stretch ${sep}`} />
            <div
              className={`${customNameCls} leading-tight uppercase tracking-wider font-bold ${subColor} break-words pl-px`}
              style={{ fontFamily: "'Chivo'" }}
              title={sideText}
            >
              {sideText}
            </div>
          </>
        ) : null}
      </div>
    );
  }

  if (nameText) {
    const ini = initialsFromDisplayName(nameText) || "?";
    const boxCls = inverted
      ? "border-white/30 bg-white/10 text-white"
      : "border-[#0F2D5C]/20 bg-[#F4F6F8] text-[#0F2D5C]";
    const iniSize =
      size === "xl" ? "text-2xl w-14 h-14" : size === "lg" ? "text-xl w-12 h-12" : size === "md" ? "text-lg w-10 h-10" : "text-base w-9 h-9";
    const nameCls =
      size === "xl" ? "text-sm" : size === "lg" ? "text-[13px]" : size === "md" ? "text-[11px]" : s.sub;
    const sub = (customSubtitle || "").trim();
    return (
      <div className={`flex items-center ${s.wrap}`} data-testid="brand-text-mark">
        <div
          className={`flex shrink-0 items-center justify-center rounded-sm border font-black ${boxCls} ${iniSize}`}
          style={{ fontFamily: "'Chivo'" }}
          aria-hidden
        >
          {ini}
        </div>
        <div className={`w-px self-stretch ${sep}`} />
        <div className="min-w-0 overflow-visible">
          <div
            className={`${nameCls} leading-tight uppercase tracking-wider font-bold ${subColor} break-words pl-px`}
            style={{ fontFamily: "'Chivo'" }}
            title={nameText}
          >
            {nameText}
          </div>
          {sub ? (
            <div className={`${s.sub} mt-0.5 font-mono normal-case tracking-normal ${subColor}`}>{sub}</div>
          ) : null}
        </div>
      </div>
    );
  }

  if (!showDefault) {
    return <div className={s.empty} data-testid="logo-empty" aria-hidden />;
  }

  return (
    <div className={`flex items-center ${s.wrap}`} data-testid="bnt-logo">
      <div className="flex items-baseline">
        <span className={`${s.letters} font-black tracking-tight ${blue}`} style={{ fontFamily: "'Chivo'", letterSpacing: "-0.04em" }}>B</span>
        <span className={`${s.letters} font-black tracking-tight ${green}`} style={{ fontFamily: "'Chivo'", letterSpacing: "-0.04em" }}>N</span>
        <span className={`${s.letters} font-black tracking-tight ${blue}`} style={{ fontFamily: "'Chivo'", letterSpacing: "-0.04em" }}>T</span>
      </div>
      <div className={`w-px self-stretch ${sep}`} />
      <ProductTagline sizeKey={size} subColor={subColor} subAccent={subAccent} />
    </div>
  );
}

export function PageHeader({ title, subtitle, breadcrumb, right, compact = false }) {
  return (
    <div
      className={`border-b border-slate-200 bg-white px-4 sm:px-8 ${compact ? "py-3" : "py-5"}`}
      data-testid={compact ? "page-header-compact" : "page-header"}
    >
      {breadcrumb && (
        <div className="text-[11px] text-slate-500 flex items-center gap-1 mb-2 uppercase tracking-wider">
          {breadcrumb.map((b, i) => (
            <span key={i} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3 h-3" />}
              {b}
            </span>
          ))}
        </div>
      )}
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1
            className={`font-bold tracking-tight text-[#0F2D5C] ${compact ? "text-xl" : "text-2xl"}`}
            style={{ fontFamily: "'Chivo'" }}
          >
            {title}
          </h1>
          {subtitle && (
            <p className={`text-slate-500 mt-0.5 ${compact ? "text-xs leading-snug" : "text-sm"}`}>{subtitle}</p>
          )}
        </div>
        {right}
      </div>
    </div>
  );
}
